import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { isAdmin, useIdentity } from '@/entities/session';
import { LogoutButton } from '@/features/auth';
import { CopilotLauncher } from '@/features/copilot';
import { GuideLauncher } from '@/features/guide';
import { NotificationBell } from '@/features/notifications';
import { useEmbedded } from '@/shared/embedded';
import { SCREEN_PATHS } from '@/shared/routes';
import { useScreenMotion } from '@/shared/ui/motion';

import { initialsOf } from './initials';
import { StartupSurface } from './StartupSurface';
import { groupIcon } from './navIcons';
import { ThemeToggle } from './ThemeToggle';
import { EXACT_MATCH_ENTRIES, NAV_GROUPS, groupForPath } from './navigation';

/* The application frame: a 72px icon rail of the eight groups, a top bar carrying the current
 * group's pages as tabs, and the page.
 *
 * **Why the grouped sidebar became a rail and tabs.** The sidebar listed every one of the
 * twenty-seven screens down the left, so the frame spent 248px on a list read top to bottom. The
 * redesign keeps the same eight groups and the same entries (`navigation.ts` is untouched as data)
 * and splits the two levels: the rail answers "which part of the product", the tabs "which page in
 * it". A reader moving between Rules, Executions and Scores never leaves the top bar.
 *
 * **A rail tile links to its group's first page and is marked for the group, not the page.**
 * `aria-current="true"` rather than `"page"`: the tile stands for a section, and the tab beside the
 * title is the page. Only one element on screen may claim to *be* the page.
 *
 * **No tile carries a count.** The Quality tile once showed the number of active critical
 * incidents; it was removed at the product owner's request. The bell already says what is waiting
 * for the reader, and a second count in the rail was one more number on every page.
 *
 * **Admin stays hidden unless the caller holds `grant:authz.role`**, as before — see `isAdmin`. The
 * route refuses too (`RequireAdmin` in `App.tsx`); hiding a tile is not access control.
 *
 * The skip link stays first in tab order, and it matters as much as ever: a keyboard user should not
 * walk nine rail tiles and a row of tabs to reach a dense table.
 */

const ADMIN_ONLY_GROUPS: ReadonlySet<string> = new Set(['admin']);

/** A file under `public/brand/`, resolved against the build's base.
 *
 * Standalone the base is `/` and this is the same `/brand/...` the markup always had. The Suite remote
 * is built under `/remotes/lumiq/<version>/`, where a root-relative `/brand/...` would ask the shell's
 * origin for a file only Lumiq's build folder holds. */
function brandAsset(file: string): string {
  return `${import.meta.env.BASE_URL}brand/${file}`;
}

function AccountMenu(): ReactElement {
  const { t } = useTranslation();
  const identity = useIdentity();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event: PointerEvent): void => {
      if (box.current !== null && !box.current.contains(event.target as Node))
        setOpen(false);
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointer);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const displayName = identity.data?.display_name;
  const roles = identity.data?.roles ?? [];
  const roleText = roles.length === 0 ? t('shell.user.roleless') : roles.join(', ');

  return (
    <div className="lq-account" ref={box}>
      {displayName === undefined ? null : (
        <button
          type="button"
          className="lq-account__disc"
          aria-expanded={open}
          aria-haspopup="true"
          aria-label={t('shell.user.label', { name: displayName })}
          title={`${displayName} · ${roleText}`}
          onClick={() => {
            setOpen(!open);
          }}
        >
          <span aria-hidden="true">{initialsOf(displayName)}</span>
        </button>
      )}
      {/* **Mounted from the first render, and hidden while closed.** The theme switch lives here, and
          it is also what applies the stored theme when the app loads — mounting it only once the
          identity arrived, or only while the menu is open, would show a reader who chose the dark
          theme the light one first. `hidden` keeps it out of the accessibility tree and tab order. */}
      <div className="lq-account__menu" hidden={!open || displayName === undefined}>
        <div className="lq-account__who">
          <span className="lq-account__name">{displayName}</span>
          <span className="lq-account__role">{roleText}</span>
        </div>
        <ThemeToggle />
        <LogoutButton />
      </div>
    </div>
  );
}

export function AppShell(): ReactElement {
  const { t } = useTranslation();
  const location = useLocation();
  const identity = useIdentity();
  const groups = isAdmin(identity.data)
    ? NAV_GROUPS
    : NAV_GROUPS.filter((group) => !ADMIN_ONLY_GROUPS.has(group.id));
  const activeId = groupForPath(location.pathname, NAV_GROUPS);
  const active = NAV_GROUPS.find((group) => group.id === activeId) ?? NAV_GROUPS[0];
  const main = useScreenMotion<HTMLElement>(location.pathname);

  /* What the page is about, for the Copilot's "Asking about …" line: the tab the reader is on, or
     the group when no tab claims the location (a detail page, an off-rail screen). */
  const currentEntry = active?.entries.find((entry) =>
    EXACT_MATCH_ENTRIES.has(entry.to)
      ? location.pathname === entry.to
      : location.pathname === entry.to || location.pathname.startsWith(`${entry.to}/`),
  );
  const context = t(currentEntry?.labelKey ?? active?.labelKey ?? 'nav.home');

  /* **Inside the Suite shell, the frame around Lumiq is the shell's.** It already draws the suite's
     brand, the user menu, the theme switch and sign-out, and it signs everybody in before Lumiq is
     loaded — so Lumiq's own copies of those, and the full-screen startup surface (a `position:
     fixed` layer that would cover the whole suite page), are not rendered. Lumiq's own navigation,
     search, guide, notifications and Copilot stay: they are this product's, not the suite's. */
  const embedded = useEmbedded();
  const navigate = useNavigate();

  /* The shell titles the browser tab "<title> · Lumiq Data AI · Suite"; a product never writes
     `document.title` itself. Standalone Lumiq has never set a per-screen title, so neither does
     this outside the shell. */
  useEffect(() => {
    embedded?.setTitle(context);
  }, [embedded, context]);

  return (
    <div className="lq-shell" data-embedded={embedded === null ? undefined : 'true'}>
      {embedded === null ? <StartupSurface /> : null}
      <a
        className="lq-skip"
        href="#lumiq-main"
        onClick={(event) => {
          /* Embedded, the address bar is the shell's: a fragment jump would write `#lumiq-main`
             into the suite's URL. Moving focus is what the skip link is for either way. */
          if (embedded === null) return;
          event.preventDefault();
          document.getElementById('lumiq-main')?.focus();
        }}
      >
        {t('shell.skip')}
      </a>

      <nav className="lq-rail" aria-label={t('shell.nav.label')}>
        {embedded === null ? (
          <Link
            className="lq-rail__brand"
            to={SCREEN_PATHS.home}
            aria-label={t('shell.homeLabel')}
            title={t('shell.productName')}
          >
            {/* The logo mark. Decorative: the link's own label names where it goes. */}
            <img
              className="lq-rail__brandMark"
              src={brandAsset('lumiq-mark.png')}
              alt=""
              width={36}
              height={16}
            />
          </Link>
        ) : null}
        <ul className="lq-rail__groups">
          {groups.map((group) => {
            const first = group.entries[0];
            if (first === undefined) return null;
            const here = group.id === activeId;
            const label = t(group.labelKey);
            return (
              <li key={group.id}>
                <Link
                  className="lq-rail__tile"
                  to={first.to}
                  data-group={group.id}
                  aria-current={here ? 'true' : undefined}
                  aria-label={label}
                  title={label}
                >
                  {groupIcon(group.id)}
                </Link>
              </li>
            );
          })}
        </ul>
        <div className="lq-rail__spacer" />
        {/* Not rendered inside the shell, and not merely hidden: it holds `ThemeToggle`, which
            writes `data-theme` on `<html>` and `localStorage` the moment it mounts, and the theme
            there is the shell's. */}
        {embedded === null ? <AccountMenu /> : null}
      </nav>

      <div className="lq-frame">
        <header className="lq-topbar">
          <div className="lq-topbar__brand">
            {embedded === null ? (
              <>
                {/* The "LUMIQ DATA" wordmark and the AI badge, as the design draws the top bar. */}
                <span className="lq-brand">
                  <img
                    className="lq-brand__wordmark"
                    src={brandAsset('lumiq-wordmark.png')}
                    alt={t('brand.logoLabel')}
                    width={121}
                    height={16}
                  />
                  <span className="lq-brand__mark">{t('shell.brandMark')}</span>
                </span>
                <span className="lq-topbar__sep" aria-hidden="true">
                  /
                </span>
              </>
            ) : null}
            <span className="lq-topbar__group">
              {t(active?.labelKey ?? 'nav.section.home')}
            </span>
          </div>

          <nav
            className="lq-topbar__tabs"
            aria-label={t('shell.nav.pages', {
              group: t(active?.labelKey ?? 'nav.section.home'),
            })}
          >
            {active?.entries.map((entry) => (
              <NavLink
                key={entry.to}
                className="lq-topbar__tab"
                to={entry.to}
                end={EXACT_MATCH_ENTRIES.has(entry.to)}
              >
                {t(entry.labelKey)}
              </NavLink>
            ))}
          </nav>

          <div className="lq-topbar__spacer" />

          {/* Search navigates to the catalogue's own search, the one surface with a server-side
              query parameter. Deliberately not a cross-entity palette: no endpoint answers across
              datasets, rules, incidents and contracts, and searching one while implying all five
              would be the fake behaviour the product rules out. */}
          <label className="lq-search">
            <svg
              className="lq-search__icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
              focusable="false"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input
              className="lq-search__input"
              type="search"
              placeholder={t('shell.search.placeholder')}
              aria-label={t('shell.search.label')}
              onKeyDown={(event) => {
                if (event.key !== 'Enter') return;
                const term = event.currentTarget.value.trim();
                if (term === '') return;
                const target = `${SCREEN_PATHS.datasets}?q=${encodeURIComponent(term)}`;
                /* Inside the shell a full-page navigation would leave the suite for a path that
                   is not Lumiq's on the shell's origin, so the in-memory router takes it and the
                   shell mirrors it into the address bar. Standalone keeps the full load it has
                   always made. */
                if (embedded !== null) navigate(target);
                else window.location.assign(target);
              }}
            />
          </label>

          <GuideLauncher />
          <NotificationBell />
          <CopilotLauncher group={activeId} context={context} />
        </header>

        <main className="lq-main" id="lumiq-main" ref={main}>
          <div className="lq-content">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
